import { Component, Inject, InjectionToken, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CotecnaUserService } from "@cotecna/session";
import { InspectionFormService } from "src/app/inspection/services/inspection-form-service/inspection-form.service";
import { ExternalRedirectionService } from "src/app/shared/external-redirection-service/external-redirection.service";
import { delay } from "src/app/testing/base-helpers/delay.helper";
import { environment } from "src/environments/environment";
import { LoginSource } from "../models/user";
import { UserService } from "../services/user.service";
import { OpenNewInspectionRetryConfiguration } from "./open-new-inspection-retry-configuration";

const EXTERNAL_INSPECTION_ID_QUERY_PARAM = "external-inspection-id";

export const OPEN_NEW_INSPECTION_RETRY_CONFIGURATION =
  new InjectionToken<OpenNewInspectionRetryConfiguration>(
    "retry configuration for opening inspections coming from ComDiv",
  );

@Component({
    selector: "im-open-new-inspection",
    templateUrl: "./open-new-inspection.component.html",
    styleUrls: ["./open-new-inspection.component.scss"],
    standalone: false
})
export class OpenNewInspectionComponent implements OnInit {
  private externalInspectionId: string;

  constructor(
    @Inject(OPEN_NEW_INSPECTION_RETRY_CONFIGURATION)
    private retryConfiguration: OpenNewInspectionRetryConfiguration,
    private connectedUserService: CotecnaUserService,
    private router: Router,
    private route: ActivatedRoute,
    private redirectionService: ExternalRedirectionService,
    private inspectionService: InspectionFormService,
    private userService: UserService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.userIsLoggedIn()) {
      await this.setUser();
    }

    this.externalInspectionId = this.getExternalInspectionId();

    if (this.externalInspectionId) {
      this.openNewInspection();
    } else {
      this.navigateToInspectionList();
    }
  }

  async setUser() {
    const user = await this.userService.getCurrentUser();
    user.loginSource = LoginSource.internal;
    this.connectedUserService.setUser(user);
  }

  private navigateToInspectionList() {
    this.router.navigate(["inspections"]);
  }

  private getExternalInspectionId() {
    return this.route.snapshot.queryParamMap.get(
      EXTERNAL_INSPECTION_ID_QUERY_PARAM,
    );
  }

  private userIsLoggedIn() {
    return !!this.route.snapshot.queryParamMap.get("login");
  }

  private openNewInspection() {
    if (this.connectedUserService.isConnected()) {
      this.navigateToNewInspection();
    } else {
      this.redirectToSsoEndpoint();
    }
  }

  private redirectToSsoEndpoint() {
    this.redirectionService.redirect(
      `${environment.webApiUrl}/login/login?${EXTERNAL_INSPECTION_ID_QUERY_PARAM}=${this.externalInspectionId}`,
    );
  }

  private async navigateToNewInspection() {
    try {
      const inspectionId = await this.getInspectionId();

      this.router.navigate(["new-inspection", inspectionId]);
    } catch {
      this.navigateToInspectionList();
    }
  }

  private async getInspectionId(attempt = 1) {
    if (attempt > this.retryConfiguration.numberOfAttempts) {
      throw new Error(
        `not succeeded in retrieving inspectionId within ${this.retryConfiguration.numberOfAttempts} attempts`,
      );
    }

    const inspectionId = await this.inspectionService.getInspectionId(
      this.externalInspectionId,
    );

    if (inspectionId) {
      return inspectionId;
    } else {
      await delay(this.retryConfiguration.milliSecondsBetweenAttempts);

      return await this.getInspectionId(attempt + 1);
    }
  }
}
